import { Fragment, ReactNode } from 'react'
import { isUndefined, kebabCase } from 'lodash'
import { Area, Line } from 'recharts'

import { MAIN_DATA_KEY } from 'modules/Monitoring/consts/chart'
import { ChartData, ChartItemWitSelection } from 'modules/Monitoring/types'
import { makeSafeDataKeyExtractor } from 'modules/Monitoring/utils/charts/extractSafeDataKey'

import { LineChartDot } from './LineChartDot'
import { LineChartLabel } from './LineChartLabel'

type RenderMainLineProps = {
  chart: ChartItemWitSelection
  entityName: string
  activeLineName?: string
  additionActiveLineName?: string
  primaryLabel?: string
  additionLabel?: string
  tooltipPayload?: ChartData[0]
  showDots: boolean
  onHighlight: (name?: string) => void
}

export const renderMainLine = ({
  chart,
  activeLineName,
  additionActiveLineName,
  primaryLabel,
  additionLabel,
  entityName,
  tooltipPayload,
  showDots,
  onHighlight,
}: RenderMainLineProps): ReactNode => {
  const { name, value, color, selected } = chart
  const areaGradientName = `area-color-${kebabCase(value)}`

  const hasActiveLine = !isUndefined(activeLineName)
  const hasAdditionalActiveLine = !isUndefined(additionActiveLineName)
  const isHighlighted = hasActiveLine && activeLineName === name
  const shouldBeFaded =
    (hasActiveLine && activeLineName !== name) ||
    (hasAdditionalActiveLine && additionActiveLineName !== name)
  // Toggling `hide` re-registers the series, which makes Recharts remount every curve
  // and lose the hover state of the one under the pointer.
  const isAreaShown =
    (showDots || hasActiveLine || hasAdditionalActiveLine) && !shouldBeFaded

  const handleChartEnter = () => {
    onHighlight(name)
  }
  const handleChartLeave = () => {
    onHighlight(undefined)
  }
  return (
    <Fragment key={value}>
      <defs>
        <linearGradient id={areaGradientName} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.15} />
          <stop offset="100%" stopColor={color} stopOpacity={0.01} />
        </linearGradient>
      </defs>

      <Area
        type="monotone"
        dataKey={makeSafeDataKeyExtractor(MAIN_DATA_KEY, value)}
        stroke="none"
        fill={`url(#${areaGradientName})`}
        fillOpacity={isAreaShown ? 1 : 0}
        pointerEvents={isAreaShown ? undefined : 'none'}
        hide={!selected}
        activeDot={false}
        isAnimationActive={false}
        onMouseEnter={handleChartEnter}
        onMouseLeave={handleChartLeave}
      />

      <Line
        name={value}
        dataKey={makeSafeDataKeyExtractor(MAIN_DATA_KEY, value)}
        stroke={color}
        hide={!selected}
        strokeWidth={2.5}
        strokeOpacity={shouldBeFaded ? 0.3 : undefined}
        type="monotone"
        pointerEvents={hasActiveLine && name !== activeLineName ? 'none' : undefined}
        activeDot={false}
        dot={({ key, ...props }) =>
          shouldBeFaded ? (
            // recharts Line.dot requires Element to be returned
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <></>
          ) : (
            <LineChartDot
              key={key}
              {...props}
              dataKeyProperty={value}
              entityName={entityName}
              primaryLabel={primaryLabel}
              additionLabel={additionLabel}
              isAddition={false}
              isShown={isHighlighted || showDots}
              isTooltipOpen={Boolean(tooltipPayload) && props.payload === tooltipPayload}
            />
          )
        }
        label={props =>
          activeLineName === name || additionActiveLineName === name || showDots ? (
            <LineChartLabel {...props} color={color} />
          ) : (
            // recharts Line.label requires Element to be returned
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <></>
          )
        }
        isAnimationActive={false}
        onMouseEnter={handleChartEnter}
        onMouseLeave={handleChartLeave}
      />
    </Fragment>
  )
}
